/**
 * Same-origin `/api`-calling sibling of `claudeChatProvider.ts` (Story
 * 015.3a, AC2 option A). Implements the same `ChatProvider` interface so
 * `ChatView.vue` needs zero changes, but talks to this app's own backend
 * (`src/backend/app.ts`) over `fetch` instead of constructing a
 * browser-side Anthropic client — the CORS root-cause fix (R023).
 *
 * The key is supplied per-request via the `x-anthropic-api-key` header,
 * matching `src/backend/config.ts`'s `API_KEY_HEADER` exactly.
 */
import type { AssistantTurn, ChatProvider, ChatTurnInput, StreamUpdate } from './chatProvider';

const API_KEY_HEADER = 'x-anthropic-api-key';

type StreamChunk =
  | { kind: 'delta'; text: string; index: number }
  | {
      kind: 'message_stop';
      stopReason: string;
      usage: { inputTokens: number; outputTokens: number };
    };

export interface BackendChatProviderConfig {
  apiKey: string;
  baseUrl?: string;
  model?: string;
  maxTokens?: number;
}

/** A rejected `AbortSignal` fetch must propagate as-is, not as a network failure. */
function isAbortError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Maps a rejected `fetchImpl` call (no HTTP response at all — DNS failure,
 * connection refused, etc.) to a masked generic message (R009/R021): the
 * raw fetch/TypeError internals never reach the caller. A genuine abort is
 * not a network failure and is rethrown unchanged so its `name` survives.
 */
function mapNetworkError(err: unknown): unknown {
  if (isAbortError(err)) return err;
  return new Error('Backend unavailable.');
}

/**
 * Uses the backend's own already-masked `.error` field verbatim (R009/R021)
 * — never reconstructed. A non-JSON body (e.g. an nginx/Ingress error page)
 * must not leak a raw `SyntaxError`; it falls back to the same generic
 * message `backendHealthProvider.ts` uses for the identical scenario.
 */
async function errorFromResponse(res: Response): Promise<Error> {
  try {
    const body = (await res.json()) as { error: string };
    return new Error(body.error);
  } catch {
    return new Error('Backend unavailable.');
  }
}

export function makeBackendChatProvider(
  config: BackendChatProviderConfig,
  fetchImpl: typeof fetch = fetch,
): ChatProvider {
  const base = config.baseUrl ?? '';

  async function post(path: string, input: ChatTurnInput): Promise<Response> {
    try {
      return await fetchImpl(`${base}${path}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          [API_KEY_HEADER]: config.apiKey,
        },
        body: JSON.stringify({
          messages: input.history,
          system: input.systemPrompt,
          ...(config.model ? { model: config.model } : {}),
          ...(config.maxTokens ? { maxTokens: config.maxTokens } : {}),
        }),
        signal: input.signal,
      });
    } catch (err) {
      throw mapNetworkError(err);
    }
  }

  return {
    async complete(input: ChatTurnInput): Promise<AssistantTurn> {
      const res = await post('/api/messages', input);
      if (!res.ok) throw await errorFromResponse(res);
      let body: {
        text: string;
        inputTokens: number;
        outputTokens: number;
        stopReason: string;
      };
      try {
        body = (await res.json()) as typeof body;
      } catch {
        throw new Error('Backend unavailable.');
      }
      return {
        text: body.text,
        inputTokens: body.inputTokens,
        outputTokens: body.outputTokens,
        stopReason: body.stopReason,
      };
    },
    async stream(
      input: ChatTurnInput,
      onDelta: (chunk: StreamUpdate) => void,
    ): Promise<AssistantTurn> {
      const res = await post('/api/messages/stream', input);
      if (!res.ok) throw await errorFromResponse(res);

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let text = '';
      let inputTokens = 0;
      let outputTokens = 0;
      let stopReason = 'end_turn';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          const dataLine = lines.find((line) => line.startsWith('data: '));
          if (!dataLine) continue;
          let parsed: unknown;
          try {
            parsed = JSON.parse(dataLine.slice('data: '.length));
          } catch {
            throw new Error('Backend unavailable.');
          }
          if (lines.some((line) => line.startsWith('event: error'))) {
            throw new Error((parsed as { error: string }).error);
          }
          const chunk = parsed as StreamChunk;
          if (chunk.kind === 'delta') {
            text += chunk.text;
            onDelta({ delta: chunk.text });
          } else if (chunk.kind === 'message_stop') {
            inputTokens = chunk.usage.inputTokens;
            outputTokens = chunk.usage.outputTokens;
            stopReason = chunk.stopReason;
          }
        }
      }

      return { text, inputTokens, outputTokens, stopReason };
    },
  };
}
